import { useState, useMemo, useEffect } from "react"

const useTableData = (rawData, config = {}) => {
  const {
    defaultSortField = "",
    defaultSortDirection = "",
    defaultItemsPerPage = 20,
    searchableFields = [],
    filteredFields = {}
  } = config

  const [sortField, setSortField] = useState(defaultSortField)
  const [sortDirection, setSortDirection] = useState(defaultSortDirection)
  const [searchQuery, setSearchQuery] = useState("")
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, itemsPerPage, filteredFields])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const filteredAndSortedData = useMemo(() => {
    let data = [...rawData]

    // --- Filtrování podle filtru ---
    if (filteredFields && Object.keys(filteredFields).length > 0) {
      // Středisko (Firebird objednávky: filtrujeme podle prefixu)
      if (filteredFields.stredisko_prefix) {
        data = data.filter(
          (item) => String(item.stredisko_prefix) === String(filteredFields.stredisko_prefix)
        )
      }

      // Stav objednávky (finální) – preferuj stav_key, fallback stav_id
      if (filteredFields.stav_key) {
        data = data.filter(
          (item) => String(item.stav_key) === String(filteredFields.stav_key)
        )
      } else if (filteredFields.stav_id !== undefined && filteredFields.stav_id !== null && filteredFields.stav_id !== "") {
        data = data.filter(
          (item) => String(item.stav) === String(filteredFields.stav_id)
        )
      }

      // Uživatel (Firebird: LOG_USER textově)
      if (filteredFields.uzivatel) {
        data = data.filter(
          (item) => String(item.uzivatel) === String(filteredFields.uzivatel)
        )
      }
      // SPZ
      if (filteredFields.spz) {
        data = data.filter(item => 
          String(item.spz).toUpperCase() === String(filteredFields.spz).toUpperCase()
        )
      }
      // Datum pořízení (pole "vytvoreno")
      if (filteredFields.datumOperator) {
        const parseDate = (str) => {
          const [d, m, y] = str.split('.').map(Number)
          return new Date(y, m - 1, d) // lokální datum
        }
        const toKey = (dt) => (
          dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate()
        )
        const d1 = filteredFields.datum1 ? parseDate(filteredFields.datum1) : null
        const d2 = filteredFields.datum2 ? parseDate(filteredFields.datum2) : null
        const d1Key = d1 ? toKey(d1) : null
        const d2Key = d2 ? toKey(d2) : null
        data = data.filter(item => {
          const itemKey = toKey(new Date(item.vytvoreno))
          switch (filteredFields.datumOperator) {
            case '..':
              return itemKey >= d1Key && itemKey <= d2Key
            case '<':
              return itemKey < d1Key
            case '<=':
              return itemKey <= d1Key
            case '>':
              return itemKey > d1Key
            case '>=':
              return itemKey >= d1Key
            case '=':
              return itemKey === d1Key
            default:
              return true
          }
        })
      }
      // Celková částka (pole "celkem")
      if (filteredFields.cenaOperator) {
        const c1 = filteredFields.cena1 ?? null
        const c2 = filteredFields.cena2 ?? null
        data = data.filter(item => {
          const itemCena = Number(item.celkem)
          switch (filteredFields.cenaOperator) {
            case '..': // rozsah
              return itemCena >= c1 && itemCena <= c2
            case '<':
              return itemCena < c1
            case '<=':
              return itemCena <= c1
            case '>':
              return itemCena > c1
            case '>=':
              return itemCena >= c1
            case '=':
              return itemCena === c1
            default:
              return true
          }
        })
      }
      // Číslo zakázky (pole "cislo")
      if (filteredFields.cisloOperator) {
        data = data.filter(item => {
          const itemCislo = String(item.cislo).toUpperCase()
          const filteredCislo = filteredFields.cislo.toUpperCase()
          switch (filteredFields.cisloOperator) {
            case 'startsWith':
              return itemCislo.startsWith(filteredCislo)
            case '=':
              return itemCislo === filteredCislo
            default:
              return true
          }
        })
      }
      // IČ zákazníka
      if (filteredFields.icoOperator) {
        data = data.filter(item => {
          const itemIco = String(item.ico).toUpperCase()
          const filteredIco = filteredFields.ico.toUpperCase()
          switch (filteredFields.icoOperator) {
            case 'startsWith':
              return itemIco.startsWith(filteredIco)
            case '=':
              return itemIco === filteredIco
            default:
              return true
          }
        })
      }
      // IČ zákazníka
      if (filteredFields.zakaznikOperator) {
        data = data.filter(item => {
          const itemZakaznik = String(item.zakaznik).toUpperCase()
          const filteredZakaznik = filteredFields.zakaznik.toUpperCase()
          switch (filteredFields.zakaznikOperator) {
            case 'startsWith':
              return itemZakaznik.startsWith(filteredZakaznik)
            case '=':
              return itemZakaznik === filteredZakaznik
            default:
              return true
          }
        })
      }

    }

    // Filtrování podle hledání
    if (searchQuery) {
      data = data.filter((item) =>
        searchableFields.some((field) => {
          const value = item[field]
          if (value === undefined || value === null) return false

          // Datumová pole – převedeme na český formát
          if (field === "vytvoreno" || field === "upraveno") {
            const date = new Date(value)

            const formattedStrict = date.toLocaleDateString("cs-CZ", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }) // např. 01. 07. 2025

            const formattedLoose = `${date.getDate()}.${
              date.getMonth() + 1
            }.${date.getFullYear()}`
            // např. 1.7.2025

            const cleanedSearch = searchQuery.replace(/\s/g, "").toLowerCase()
            const cleanedStrict = formattedStrict
              .replace(/\s/g, "")
              .toLowerCase()
            const cleanedLoose = formattedLoose
              .replace(/\s/g, "")
              .toLowerCase()

            return (
              cleanedStrict.includes(cleanedSearch) ||
              cleanedLoose.includes(cleanedSearch)
            )
          }

          // Číslo (např. cena) – porovnáme lokalizovanou verzi
          if (field === "cena" || field === "celkem") {
            const formattedCurrency = Number(value).toLocaleString("cs-CZ", {
              style: "currency",
              currency: "CZK",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })

            const formattedPlain = Number(value).toLocaleString("cs-CZ", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })

            // Vyčistíme všechny mezery (včetně nezlomitelných) a porovnáme
            const cleanedSearch = searchQuery
              .replace(/\s/g, "")
              .replace(/\u00A0/g, "")
              .toLowerCase()
            const cleanedCurrency = formattedCurrency
              .replace(/\s/g, "")
              .replace(/\u00A0/g, "")
              .toLowerCase()
            const cleanedPlain = formattedPlain
              .replace(/\s/g, "")
              .replace(/\u00A0/g, "")
              .toLowerCase()

            return (
              cleanedCurrency.includes(cleanedSearch) ||
              cleanedPlain.includes(cleanedSearch)
            )
          }

          // Stav (aktivní / neaktivní)
          if (field === "stav") {
            const cleanedSearch = searchQuery.trim().toLowerCase()
            const stavString = value === 1 ? "aktivní" : "blokovaný"
            return stavString.includes(cleanedSearch)
          }

          // Role (admin / technik / mechanik)
          if (field === "role") {
            const cleanedSearch = searchQuery.trim().toLowerCase()
            const stavString = 
                value === 1 ? "admin" 
                : value === 2 ? "technik"
                : value === 3 ? "mechanik"
                : ""
            return stavString.includes(cleanedSearch)
          }

          
          // Textové pole – obecné porovnání
          return value
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        })
      )
    }

    // Řazení
    if (sortField) {
      data.sort((a, b) => {
        const aVal = a[sortField]
        const bVal = b[sortField]

        // Datumové řazení
        if (sortField === "vytvoreno" || sortField === "upraveno") {
          const aDate = new Date(aVal)
          const bDate = new Date(bVal)
          return sortDirection === "asc" ? aDate - bDate : bDate - aDate
        }

        // Číselné řazení
        if (sortField === "cena") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal
        }

        // Textové řazení
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortDirection === "asc"
            ? aVal.localeCompare(bVal, "cs", { sensitivity: "base" })
            : bVal.localeCompare(aVal, "cs", { sensitivity: "base" })
        }

        // Fallback – ostatní typy
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      })
    }

    return data
  }, [rawData, searchQuery, sortField, sortDirection, filteredFields])

  const totalItems = filteredAndSortedData.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const displayedData = filteredAndSortedData.slice(startIndex, endIndex)

  return {
    data: displayedData,
    sortField,
    sortDirection,
    searchQuery,
    itemsPerPage,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    handleSort,
    setSearchQuery,
    setItemsPerPage,
    setCurrentPage,
    handlePrevPage: () => currentPage > 1 && setCurrentPage((prev) => prev - 1),
    handleNextPage: () => currentPage < totalPages && setCurrentPage((prev) => prev + 1),
  }
}

export default useTableData
